// Cierre de la entrega: artículos + observaciones + DOS firmas (repartidor/cliente) → acuse PDF.

import { useState } from 'react'
import { SignaturePad } from '@/components/SignaturePad'
import { Button } from '@/components/ui/Button'
import { AcuseActions } from '@/components/AcuseActions'
import { createAcuse, type Acuse } from '@/services/acuse'

export default function AcuseFlow({
  deliveryId,
  onDone,
}: {
  deliveryId: string
  onDone?: () => void
}) {
  const [items, setItems] = useState('')
  const [observations, setObservations] = useState('')
  const [clientName, setClientName] = useState('')
  const [clientSignature, setClientSignature] = useState<string | null>(null)
  const [driverSignature, setDriverSignature] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [acuse, setAcuse] = useState<Acuse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    if (!clientSignature || !clientName || !driverSignature) return
    setBusy(true)
    setError(null)
    try {
      const created = await createAcuse(deliveryId, {
        items_delivered: items.split('\n').map((a) => a.trim()).filter(Boolean),
        observations: observations || null,
        client_name: clientName,
        client_signature_b64: clientSignature,
        driver_signature_b64: driverSignature,
      })
      setAcuse(created)
      onDone?.()
    } catch (e) {
      setError((e as { message?: string })?.message ?? 'No se pudo generar el acuse.')
    } finally {
      setBusy(false)
    }
  }

  if (acuse) {
    return (
      <div className="rounded-xl bg-green-50 p-4 text-center ring-1 ring-inset ring-green-200">
        <p className="font-semibold text-green-800">✓ Acuse generado</p>
        <p className="mt-1 text-sm text-gray-600">{acuse.folio}</p>
        {acuse.pdf_url && (
          <div className="mt-3">
            <AcuseActions url={acuse.pdf_url} folio={acuse.folio} />
          </div>
        )}
      </div>
    )
  }

  const field =
    'mt-1 w-full rounded-lg border border-gray-300 p-2 text-sm shadow-sm focus:border-ds-500 focus:outline-none focus:ring-2 focus:ring-ds-500/20'
  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700">
        Artículos entregados <span className="font-normal text-gray-400">(una por línea)</span>
        <textarea className={field} rows={4} value={items} onChange={(e) => setItems(e.target.value)} />
      </label>
      <label className="block text-sm font-medium text-gray-700">
        Observaciones
        <textarea className={field} rows={2} value={observations} onChange={(e) => setObservations(e.target.value)} />
      </label>

      {/* Firma de quien ENTREGA (DS / técnico que cierra) */}
      <div className="rounded-xl border border-gray-200 p-3">
        <p className="text-sm font-semibold text-gray-800">1. Firma de quien entrega (repartidor)</p>
        <p className="mb-2 text-xs text-gray-500">Tu firma como responsable de la entrega.</p>
        <SignaturePad onChange={setDriverSignature} />
      </div>

      {/* Firma de quien RECIBE (cliente) */}
      <div className="rounded-xl border border-gray-200 p-3">
        <p className="text-sm font-semibold text-gray-800">2. Firma de quien recibe (cliente)</p>
        <label className="mt-1 block text-xs font-medium text-gray-500">
          Nombre de quien recibe
          <input className={field} value={clientName} onChange={(e) => setClientName(e.target.value)} />
        </label>
        <div className="mt-2">
          <SignaturePad onChange={setClientSignature} />
        </div>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      <Button
        className="w-full"
        onClick={submit}
        disabled={!clientSignature || !driverSignature || !clientName || busy}
      >
        {busy ? 'Generando…' : 'Firmar y cerrar entrega'}
      </Button>
      {(!driverSignature || !clientSignature || !clientName) && (
        <p className="text-center text-xs text-gray-400">
          Se requieren ambas firmas y el nombre de quien recibe.
        </p>
      )}
    </div>
  )
}
